import type { ButtonBorderSides, InputBlock } from "@aswin.dev/types";
import type { RenderContext } from "../render-context";
import {
  convertMergeTagsToValues,
  escapeAttr,
  escapeCssValue,
  escapeHtml,
} from "../escape";
import { toMarginString, toPaddingString } from "../padding";
import { bgAttr } from "../utils";
import { isHiddenOnAll, getCssClassAttr } from "../visibility";

function fontFamilyDeclaration(
  fontFamily: string | undefined,
  context: RenderContext,
): string {
  if (!fontFamily) {
    return "";
  }
  const resolved = context.resolveFontFamily(fontFamily);
  return `font-family:${escapeCssValue(resolved)};`;
}

function inputBorderCss(
  widthPx: number,
  color: string,
  sides: ButtonBorderSides,
): string {
  if (widthPx <= 0) {
    return "border:none;";
  }
  const w = `${widthPx}px`;
  const c = escapeCssValue(color);
  if (sides === "all") {
    return `border:${w} solid ${c};`;
  }
  const z = "0";
  const tw = sides === "top" ? w : z;
  const rw = sides === "right" ? w : z;
  const bw = sides === "bottom" ? w : z;
  const lw = sides === "left" ? w : z;
  return `border-style:solid;border-color:${c};border-top-width:${tw};border-right-width:${rw};border-bottom-width:${bw};border-left-width:${lw};`;
}

function mergePlain(text: string): string {
  return escapeHtml(convertMergeTagsToValues(text));
}

/**
 * Renders an input block as MJML `mj-text` wrapping label + `<input>`.
 * Interactive fields work on web; many email clients strip or ignore inputs.
 */
export function renderInput(block: InputBlock, context: RenderContext): string {
  if (isHiddenOnAll(block)) {
    return "";
  }

  const padding = toPaddingString(block.styles.padding);
  const bgColor = bgAttr(block.styles.backgroundColor, "container");
  const visibilityAttr = getCssClassAttr(block);

  const htmlInputId = `tpl-in-${block.id}`;
  const htmlInputIdAttr = escapeAttr(htmlInputId);
  const dataIdAttr = escapeAttr(block.id);

  const labelText = mergePlain(block.label);
  const placeholderPlain = convertMergeTagsToValues(block.placeholder);
  const valuePlain = convertMergeTagsToValues(block.defaultValue ?? "");

  const labelMargin = toMarginString(block.labelMargin);
  const labelPadding = toPaddingString(block.labelPadding);
  const labelStyle = [
    "display:block;",
    `margin:${labelMargin};`,
    `padding:${labelPadding};`,
    `font-size:${block.labelFontSize}px;`,
    `font-weight:${block.labelFontWeight};`,
    `color:${escapeCssValue(block.labelColor)};`,
    `text-align:${block.labelTextAlign};`,
    fontFamilyDeclaration(block.labelFontFamily, context),
  ]
    .filter(Boolean)
    .join("");

  const inputMargin = toMarginString(block.inputMargin);
  const inputPad = toPaddingString(block.inputPadding);
  const widthCss =
    block.inputWidth === "full" ? "100%" : `${block.inputWidth}px`;
  const bw = block.inputBorderWidth ?? 0;
  const borderCss = inputBorderCss(
    bw,
    block.inputBorderColor ?? "#d1d5db",
    block.inputBorderSides ?? "all",
  );

  const inputStyle = [
    "box-sizing:border-box;",
    `width:${widthCss};`,
    `margin:${inputMargin};`,
    `padding:${inputPad};`,
    `background-color:${escapeCssValue(block.inputBackgroundColor)};`,
    `color:${escapeCssValue(block.inputTextColor)};`,
    `font-size:${block.inputFontSize}px;`,
    `border-radius:${block.inputBorderRadius}px;`,
    borderCss,
    fontFamilyDeclaration(block.inputFontFamily, context),
    "outline:none;",
  ].join("");

  const typeAttr = escapeAttr(block.inputType);
  const requiredAttr = block.required ? " required" : "";
  const nameAttr =
    block.name.trim() === "" ? "" : ` name="${escapeAttr(block.name.trim())}"`;

  const placeholderStyle = `<style type="text/css">[data-tpl-input="${dataIdAttr}"]::placeholder{color:${escapeCssValue(block.inputPlaceholderColor)}!important;opacity:1;}</style>`;

  const html = `${placeholderStyle}<label for="${htmlInputIdAttr}" style="${labelStyle}">${labelText}</label><input id="${htmlInputIdAttr}"${nameAttr} type="${typeAttr}" data-tpl-input="${dataIdAttr}" placeholder="${escapeAttr(placeholderPlain)}" value="${escapeAttr(valuePlain)}" style="${inputStyle}"${requiredAttr} />`;

  return `<mj-text
  line-height="1.5"
  padding="${padding}"${bgColor}${visibilityAttr}
>${html}</mj-text>`;
}
